/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React, { useEffect, useState } from 'react';
import _get from 'lodash/get';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import { useOrderResource } from '../hooks/useOrderResource';
import Overlay from './Overlay';
import {
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SortableItem from './SortableItem';

function AssetsGrid({ assets, handleChangePosition, handleDeleteAsset }) {
  const { dragEnd, updating, progress } = useOrderResource();

  const [items, setItems] = useState(assets);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 4 pixels before activating
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setItems(assets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets.length]);

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const { sortedItems } = dragEnd(items, active, over);

      setItems(sortedItems);
      handleChangePosition(active.id, over.data.current.sortable.index);
    }
  }

  return (
    <>
      {updating && <Overlay progress={progress} />}
      <ImageList
        sx={{
          width: '100%',
          gridTemplateColumns:
            'repeat(auto-fill, minmax(128px, 1fr)) !important',
        }}
        cols={8}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items}>
            {items.map((asset) => {
              const url = `${_get(
                asset,
                'thumbnail.url'
              )}?width=128&height=128&format=png`;
              return (
                <SortableItem key={asset.id} id={asset.id}>
                  <ImageListItem key={url}>
                    <img
                      src={url}
                      alt={asset.title}
                      style={{
                        height: '128px',
                      }}
                      loading="lazy"
                    />
                    <ImageListItemBar
                      title={
                        <Typography
                          variant={'subtitle2'}
                          sx={{ fontSize: '0.8em' }}
                        >
                          {asset.title}
                        </Typography>
                      }
                      subtitle={
                        <Typography
                          variant={'subtitle2'}
                          sx={{ fontSize: '0.8em' }}
                        >
                          {asset.extraData.ref}
                        </Typography>
                      }
                      actionIcon={
                        <IconButton
                          onClick={() => {
                            handleDeleteAsset(asset.id);
                          }}
                          sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                          aria-label={`Delete ${asset.title} from collection`}
                        >
                          <DeleteIcon color={'action'} />
                        </IconButton>
                      }
                      position="below"
                    />
                  </ImageListItem>
                </SortableItem>
              );
            })}
          </SortableContext>
        </DndContext>
      </ImageList>
    </>
  );
}

AssetsGrid.defaultProps = {
  cols: 7,
};

export default AssetsGrid;
